import { Controller } from "@hotwired/stimulus"

// Hides the viewer's own name from the reply-collision banner and rewrites the
// sentence after a live replace. Claim and release stay on HTTP; the banner
// arrives on the conversation Turbo stream the frame already subscribes to.
export default class extends Controller {
  static targets = [ "banner", "copy", "nameTemplate", "actions", "claimButton", "releaseButton" ]
  static values = { userId: Number, url: String, canReply: Boolean }

  connect() {
    this.observer = new MutationObserver((mutations) => {
      if (this.bannerMutated(mutations)) this.reconcile()
    })
    this.observer.observe(this.element, { childList: true, subtree: true })
    this.reconcile()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  claim(event) {
    event.preventDefault()
    this.submitClaim("POST")
  }

  release(event) {
    event.preventDefault()
    this.submitClaim("DELETE")
  }

  async submitClaim(method) {
    if (!this.hasUrlValue || !this.urlValue) return

    this.setBusy(true)
    try {
      await fetch(this.urlValue, {
        method,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken()
        }
      })
    } catch {
      // The next broadcast or a retry is enough; a dropped claim must not
      // surface as a page error.
    } finally {
      this.setBusy(false)
    }
  }

  // The controller lives on .thread with presence and typers. Those strips rewrite
  // their copy on any subtree change; writing ours back would ping-pong and hang
  // the tab. Only a replace of this banner (or its children) is our signal.
  bannerMutated(mutations) {
    return mutations.some((mutation) => {
      if (this.insideBanner(mutation.target)) return true

      return [ ...mutation.addedNodes, ...mutation.removedNodes ].some((node) => (
        this.isBannerElement(node) || this.insideBanner(node)
      ))
    })
  }

  isBannerElement(node) {
    return node instanceof Element && node.classList.contains("thread-reply-collision")
  }

  insideBanner(node) {
    if (this.isBannerElement(node)) return true
    if (node instanceof Element) return Boolean(node.closest(".thread-reply-collision"))
    return this.hasBannerTarget && this.bannerTarget.contains(node)
  }

  reconcile() {
    if (!this.hasBannerTarget) return

    this.observer.disconnect()

    const userId = String(this.userIdValue)
    const claimerId = this.bannerTarget.dataset.claimerId || ""
    const ownClaim = claimerId !== "" && claimerId === userId
    const names = []
    this.bannerTarget.querySelectorAll("[data-user-id]").forEach((person) => {
      const isSelf = person.dataset.userId === userId
      this.setHidden(person, isSelf)
      if (!isSelf && person.dataset.userName) names.push(person.dataset.userName)
    })

    const copy = this.sentence({ ownClaim, claimerId, names })
    this.renderCopy(copy)
    this.setHidden(this.bannerTarget, !copy)
    if (copy) {
      this.bannerTarget.setAttribute("aria-label", copy)
    } else {
      this.bannerTarget.removeAttribute("aria-label")
    }

    if (this.hasActionsTarget) this.setHidden(this.actionsTarget, !this.canReplyValue || !copy)
    if (this.hasClaimButtonTarget) {
      this.setHidden(this.claimButtonTarget, !this.canReplyValue || ownClaim)
    }
    if (this.hasReleaseButtonTarget) {
      this.setHidden(this.releaseButtonTarget, !this.canReplyValue || !ownClaim)
    }

    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  sentence({ ownClaim, claimerId, names }) {
    if (ownClaim) return "You're taking this reply"

    const claimerName = this.bannerTarget.dataset.claimerName
    if (claimerId && claimerName) {
      const ago = compactAgo(this.bannerTarget.dataset.claimedAt)
      let text = `${claimerName} is taking this reply · ${ago}`
      const also = names.filter((name) => name !== claimerName)
      if (also.length === 1) text += ` · ${also[0]} is also drafting`
      if (also.length > 1) text += ` · ${toSentence(also)} are also drafting`
      return text
    }

    if (names.length === 0) return ""

    const ago = compactAgo(this.latestActivityAt())
    if (names.length === 1) return `${names[0]} is drafting a reply · ${ago}`
    return `${toSentence(names)} are drafting replies · ${ago}`
  }

  renderCopy(copy) {
    if (!this.hasCopyTarget) return
    if (this.copyTarget.textContent === copy) return

    this.copyTarget.textContent = copy
  }

  setHidden(element, hidden) {
    if (element.hidden === hidden) return

    element.hidden = hidden
  }

  latestActivityAt() {
    const times = []
    this.bannerTarget.querySelectorAll("[data-user-id]:not([hidden])").forEach((person) => {
      if (person.dataset.activityAt) times.push(person.dataset.activityAt)
    })
    return times.sort().at(-1)
  }

  setBusy(busy) {
    if (this.hasBannerTarget) {
      if (busy) this.bannerTarget.setAttribute("aria-busy", "true")
      else this.bannerTarget.removeAttribute("aria-busy")
    }
    if (this.hasClaimButtonTarget) this.claimButtonTarget.disabled = busy
    if (this.hasReleaseButtonTarget) this.releaseButtonTarget.disabled = busy
  }
}

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content
}

function toSentence(names) {
  if (names.length <= 1) return names[0] || ""
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`
}

function compactAgo(iso) {
  if (!iso) return "now"

  const elapsed = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 1000))
  if (elapsed < 60) return "now"
  if (elapsed < 3600) return `${Math.floor(elapsed / 60)}m ago`
  if (elapsed < 86400) return `${Math.floor(elapsed / 3600)}h ago`
  return `${Math.floor(elapsed / 86400)}d ago`
}
